import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import DotsHorizontalSvg from "@redotech/redo-icons/dots-horizontal.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import { formatShortcut } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo, useState } from "react";
import { useConversationActionToolbar } from "../hooks/use-conversation-action-toolbar";

export interface ConversationActionToolbarProps {
  conversationId: ConversationId;
  conversation: ConversationListItemResponse;
  disabled?: boolean;
}

export const ConversationActionToolbar = memo(
  function ConversationActionToolbar({
    conversationId,
    conversation,
    disabled = false,
  }: ConversationActionToolbarProps) {
    const {
      primaryAction,
      menuActions,
      conversation: ctx,
      handlePrimaryAction,
      handleMenuAction,
    } = useConversationActionToolbar(conversationId, conversation);

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    // Filter menu items by isVisible
    const visibleMenuActions = useMemo(
      () => menuActions.filter((action) => action.isVisible?.(ctx) ?? true),
      [menuActions, ctx],
    );

    const commandMenuItems = useMemo(
      (): RedoCommandMenuItem[] =>
        visibleMenuActions.map((action) => ({
          Icon: action.Icon,
          text: action.title,
          onClick: () => handleMenuAction(action.id),
          tooltip: action.shortcut
            ? formatShortcut(action.shortcut)
            : undefined,
          disabled: !(action.isEnabled?.(ctx) ?? true),
        })),
      [visibleMenuActions, ctx, handleMenuAction],
    );

    const primaryEnabled = primaryAction.isEnabled?.(ctx) ?? true;
    const primaryVisible = primaryAction.isVisible?.(ctx) ?? true;

    return (
      <Flex align="center" gap="sm">
        {commandMenuItems.length > 0 && (
          <>
            <RedoButton
              disabled={disabled}
              hierarchy="secondary"
              IconLeading={DotsHorizontalSvg}
              onClick={() => setMenuOpen(!menuOpen)}
              ref={setMenuAnchor}
              size="sm"
            />
            <RedoCommandMenu
              anchor={menuAnchor}
              items={commandMenuItems}
              open={menuOpen}
              setOpen={setMenuOpen}
            />
          </>
        )}
        {primaryVisible && (
          <Tooltip
            arrow
            title={
              primaryAction.shortcut
                ? formatShortcut(primaryAction.shortcut)
                : undefined
            }
          >
            <span>
              <RedoButton
                disabled={disabled || !primaryEnabled}
                hierarchy="primary"
                IconLeading={primaryAction.Icon}
                onClick={handlePrimaryAction}
                size="sm"
                text={primaryAction.title}
              />
            </span>
          </Tooltip>
        )}
      </Flex>
    );
  },
);
